import React, { useMemo, memo, useEffect, useCallback } from 'react';
import {
  BackHandler,
  Image,
  Platform,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSelector } from 'react-redux';

import Loading from '@common/components/Loading';
import useAppDetail from '@common/hooks/app';
import { getAllPages } from '@common/hooks/page';
import useSocketConnection from '@common/hooks/useSocketAuthentication';
import { initializeAdmob } from '@common/hooks/admod';
import { dimensionSelector } from '@common/redux/selectors/page';
import { useParams } from '@common/routes/hooks';
import { pastLocationSelector } from '@common/redux/selectors/locationHistory';
import history from '@common/routes/history';

import RenderScreen from './RenderScreen';
import useLocation from '@common/hooks/useLocation';
import useWatchRecords from '@common/hooks/useWatchRecord';
import { usePlanPermission } from '@common/hooks/usePlanPermission';
import { useCollections } from '@common/hooks/collections';

const MemoRender = memo(function RenderLayout({ appId }: { appId: string }) {
  const dimension = useSelector(dimensionSelector);
  const pastLocations = useSelector(pastLocationSelector);

  const handleDeviceBack = useCallback(() => {
    if (pastLocations.length <= 1) {
      BackHandler.exitApp();
    }
    history.goBack();
    return true;
  }, [pastLocations]);

  const { data: appInfo, isLoading: loadingApp } = useAppDetail({
    appId,
  });

  useCollections({ appId });

  const {
    data: pages,
    error,
    isLoading: loadingPage,
    isUseMap,
  } = getAllPages(appId, appInfo);

  initializeAdmob({ appInfo });
  const { isReady: initializedMap }: any = useLocation({
    isUseMap,
  });

  // Listening to event Socket
  useWatchRecords();

  const isLoadingApp = loadingApp || loadingPage || !initializedMap;

  const handleRenderScreen = () => {
    if (appInfo && pages && pages.length > 0) {
      return <RenderScreen appInfo={appInfo} />;
      // return <ShareScreenView appId={appId} />;
    } else {
      return (
        <View
          style={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            width: dimension.width,
            height: dimension.height,
            backgroundColor: 'white',
          }}
        >
          <Image
            source={require('@common/assets/images/splashScreen.png')}
            style={{ width: 200, height: 200 }}
          />
        </View>
      );
    }
  };

  useEffect(() => {
    const backPressListener = BackHandler.addEventListener(
      'hardwareBackPress',
      handleDeviceBack
    );

    return () => {
      backPressListener.remove();
    };
  }, [handleDeviceBack]);

  if (isLoadingApp) return <Loading />;

  return (
    <React.Fragment>
      {handleRenderScreen()}
      {error && (
        <View style={styles.errorWrapper}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}
    </React.Fragment>
  );
});

const styles = StyleSheet.create({
  errorWrapper: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    color: 'red',
  },
});

function Render(cusParams?: { appId: string }) {
  const params = useParams();
  const appId = useMemo(
    () => params?.appId || cusParams?.appId,
    [params?.appId, cusParams?.appId]
  );

  const { checkShowPlanModalByNumberOfRecords } = usePlanPermission();
  // check plan permission
  useEffect(() => {
    checkShowPlanModalByNumberOfRecords(appId);
  }, [checkShowPlanModalByNumberOfRecords]);

  // Safety net: the native splash screen is otherwise only hidden once app data
  // finishes loading (see RenderScreen/RenderScreen). Offline that never happens,
  // so the splash stays up and iOS kills the app with a ~20s "scene-create"
  // watchdog (0x8BADF00D). Hide it shortly after launch regardless, so the app
  // always presents UI well before the watchdog fires.
  useEffect(() => {
    if (Platform.OS === 'web') return;
    const timer = setTimeout(async () => {
      try {
        const SplashScreen = (await import('react-native-splash-screen'))
          .default;
        SplashScreen?.hide();
      } catch (error) {
        console.error('SplashScreen hide error: ', error);
      }
    }, 4000);
    return () => clearTimeout(timer);
  }, []);
  // authentication SocketIO (forced to run first !)
  const { loading: reAuthLoading } = useSocketConnection();

  if (!appId) {
    return (
      <View>
        <Text>Cannot find page</Text>
      </View>
    );
  }

  if (reAuthLoading) {
    return <Loading />;
  }

  return <MemoRender appId={appId} />;
}

export default Render;
